import { MobileOfferProvider, MobileOfferPurpose, MobileOfferTerms } from '@numeo/types'
import { MobilePlan } from '@numeo/types-core'
import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, ShieldCheck } from 'lucide-react'
import { CreateOfferForm } from './CreateOfferForm'
import { OfferButton, OfferNotice, OfferSection } from './ui'
import { modeLabel, offerDescription, offerLinkClass, offerPlans, planLabels, storeLabels } from './offerLabels'
import { OfferPrices } from './OfferPrices'
import { useOfferCreation } from './useOfferCreation'
import { mobileOfferError, mobileOffersApi } from './api'

export function CreateOfferPage() {
    const [params, setParams] = useSearchParams()
    const draftId = params.get('draft')
    const initialProviders = useRef<MobileOfferProvider[]>(
        params
            .get('stores')
            ?.split(',')
            .filter((value): value is MobileOfferProvider => value === 'google' || value === 'apple') || ['google']
    )
    const [providers, setProviders] = useState<MobileOfferProvider[]>(initialProviders.current)
    const [terms, setTerms] = useState<MobileOfferTerms>()
    const [step, setStep] = useState(1)
    const [confirmed, setConfirmed] = useState(false)
    const [loading, setLoading] = useState(!!draftId)
    const [loadError, setLoadError] = useState('')
    const loaded = useRef<string | null>(null)
    const requestedPlan = offerPlans.find((plan) => plan === params.get('plan'))
    // Drafts reload from `?draft=`, so retention mode follows the saved terms once they exist.
    const retentionPlan: MobilePlan | undefined =
        terms?.purpose === MobileOfferPurpose.CANCELLATION_RETENTION ? terms.plan : !terms && params.get('purpose') === 'retention' ? requestedPlan : undefined
    const backTo = retentionPlan ? '/billing/mobile-offers/retention' : '/billing/mobile-offers'
    const flow = useOfferCreation()
    const { setOffer, clear } = flow
    useEffect(() => {
        if (!draftId || loaded.current === draftId) return
        let active = true
        clear()
        setConfirmed(false)
        setLoadError('')
        setLoading(true)
        mobileOffersApi
            .find(draftId)
            .then((offer) => {
                if (!active) return
                loaded.current = draftId
                setOffer(offer)
                setTerms(offer.terms)
                setStep(2)
            })
            .catch((error) => {
                if (active) setLoadError(mobileOfferError(error))
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
    }, [draftId, setOffer, clear])
    const preview = (nextTerms: MobileOfferTerms, selected: MobileOfferProvider[]) => {
        setTerms(nextTerms)
        setProviders(selected)
        setConfirmed(false)
        setStep(2)
        void flow.preview(nextTerms, selected, (offer) => {
            loaded.current = offer.id
            const next: Record<string, string> = { draft: offer.id, stores: selected.join(',') }
            if (nextTerms.purpose === MobileOfferPurpose.CANCELLATION_RETENTION) Object.assign(next, { purpose: 'retention', plan: nextTerms.plan })
            setParams(next, { replace: true })
        })
    }
    const canCreate =
        !flow.busy &&
        !!flow.offer &&
        providers.length > 0 &&
        providers.every((provider) => flow.previews[provider]?.offerId === flow.offer?.id && flow.previews[provider]?.provider === provider)
    const createLabel = providers.length === 2 ? 'Create in both stores' : `Create in ${storeLabels[providers[0]] || 'store'}`
    if (loading) return <OfferNotice>Loading saved draft…</OfferNotice>
    if (loadError) return <OfferNotice error>{loadError}</OfferNotice>
    return (
        <div className="space-y-5">
            <Link to={backTo} className={offerLinkClass}>
                <ArrowLeft size={16} />
                {retentionPlan ? 'Back to retention offers' : 'Back to offers'}
            </Link>
            <div>
                <h2 className="text-xl font-semibold">{retentionPlan ? 'Create retention offer' : 'Create mobile offer'}</h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">One set of terms. Only the stores you choose.</p>
            </div>
            <ol aria-label="Creation steps" className="flex flex-wrap gap-5">
                {['Offer details', 'Review store prices', 'Create in stores'].map((label, index) => (
                    <li
                        key={label}
                        aria-current={step === index + 1 ? 'step' : undefined}
                        className={`flex items-center gap-2 text-sm ${step === index + 1 ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'}`}
                    >
                        <span
                            className={`flex h-7 w-7 items-center justify-center rounded-full ${step >= index + 1 ? 'bg-blue-600 text-white dark:bg-blue-500' : 'bg-slate-200 dark:bg-slate-800'}`}
                        >
                            {index + 1}
                        </span>
                        {label}
                    </li>
                ))}
            </ol>
            <div className="grid items-start gap-5 xl:grid-cols-3">
                <div className="min-w-0 space-y-4 xl:col-span-2">
                    {step === 1 ? (
                        <CreateOfferForm
                            key={terms ? JSON.stringify(terms) : 'new'}
                            initialTerms={terms}
                            initialProviders={providers}
                            retentionPlan={retentionPlan}
                            onContinue={preview}
                        />
                    ) : (
                        <>
                            {step === 2 && (
                                <OfferSection
                                    title="Review store prices"
                                    description="These prices come from the stores. Rounding may change the effective discount."
                                >
                                    {flow.busy && <OfferNotice>Saving the Gluon draft and loading prices for your selected stores…</OfferNotice>}
                                    {providers.map((provider) => (
                                        <div
                                            key={provider}
                                            className="space-y-3 border-t border-slate-200 pt-4 first:border-0 first:pt-0 dark:border-slate-700"
                                        >
                                            <h3 className="text-sm font-semibold">{storeLabels[provider]}</h3>
                                            {flow.previews[provider] ? (
                                                <OfferPrices prices={flow.previews[provider]!.prices} periods={terms?.discountedPeriods || 1} />
                                            ) : (
                                                <OfferNotice error={!!flow.errors[provider]}>
                                                    {flow.errors[provider] ||
                                                        (flow.busy ? 'Loading store prices…' : 'Reload prices to review this saved draft.')}
                                                </OfferNotice>
                                            )}
                                        </div>
                                    ))}
                                    {!flow.busy && !canCreate && terms && (
                                        <OfferButton variant="secondary" onClick={() => preview(terms, providers)}>
                                            Reload selected store prices
                                        </OfferButton>
                                    )}
                                    {canCreate && (
                                        <label className="flex items-start gap-3 rounded-lg bg-slate-50 p-4 text-sm leading-relaxed dark:bg-slate-800">
                                            <input
                                                type="checkbox"
                                                className="mt-1 h-4 w-4 accent-blue-600"
                                                checked={confirmed}
                                                onChange={(event) => setConfirmed(event.target.checked)}
                                            />
                                            I approve these exact prices, countries and duration in{' '}
                                            {providers.map((provider) => storeLabels[provider]).join(' and ')}. This changes live store configuration, including
                                            for test purchases.
                                        </label>
                                    )}
                                    <div className="flex flex-wrap justify-between gap-3">
                                        <OfferButton
                                            variant="secondary"
                                            disabled={flow.busy}
                                            onClick={() => {
                                                flow.clear()
                                                setConfirmed(false)
                                                setStep(1)
                                            }}
                                        >
                                            Edit details
                                        </OfferButton>
                                        <OfferButton
                                            disabled={!canCreate || !confirmed}
                                            onClick={() => {
                                                setConfirmed(false)
                                                setStep(3)
                                                void flow.create(providers)
                                            }}
                                        >
                                            {createLabel}
                                        </OfferButton>
                                    </div>
                                </OfferSection>
                            )}
                            {step === 3 && (
                                <OfferSection
                                    title={flow.busy ? 'Creating your offer' : 'Store creation results'}
                                    description="Each store is processed independently. Successful creation is preserved if the other store needs attention."
                                >
                                    {providers.map((provider) => {
                                        const result = flow.results[provider]
                                        return (
                                            <div key={provider} className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                                                <div className="flex items-center justify-between gap-3">
                                                    <h3 className="text-sm font-semibold">{storeLabels[provider]}</h3>
                                                    <span
                                                        className={`text-sm ${result?.state === 'ready' ? 'text-green-700 dark:text-green-300' : 'text-slate-500 dark:text-slate-400'}`}
                                                    >
                                                        {result?.state === 'ready' ? 'Configured' : result?.state === 'review' ? 'Needs review' : 'Creating…'}
                                                    </span>
                                                </div>
                                                {result?.message && (
                                                    <p role="status" className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                                                        {result.message}
                                                    </p>
                                                )}
                                                {provider === 'apple' && result?.state === 'ready' && (
                                                    <OfferNotice>
                                                        Next: generate a code batch in offer details before assigning this App Store offer.
                                                    </OfferNotice>
                                                )}
                                            </div>
                                        )
                                    })}
                                    {!flow.busy && flow.offer && (
                                        <Link className={offerLinkClass} to={`/billing/mobile-offers/offers/${flow.offer.id}`}>
                                            <CheckCircle2 size={16} />
                                            Open offer details & next steps
                                        </Link>
                                    )}
                                </OfferSection>
                            )}
                        </>
                    )}
                    {flow.error && <OfferNotice error>{flow.error}</OfferNotice>}
                </div>
                <aside className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
                    <h3 className="text-sm font-semibold">How this works</h3>
                    {terms && (
                        <div className="space-y-1 border-b border-slate-200 pb-4 dark:border-slate-700">
                            <p className="font-semibold">{planLabels[terms.plan]}</p>
                            <p className="text-sm">{offerDescription(terms)}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                {terms.countries.length} countries · {modeLabel(terms)}
                            </p>
                        </div>
                    )}
                    <p className="text-sm leading-relaxed text-slate-500 dark:text-slate-400">
                        Save a draft, review actual prices, then create in your selected stores. An unselected store stays unconfigured.
                    </p>
                    <div className="flex gap-2 text-sm text-slate-500 dark:text-slate-400">
                        <ShieldCheck size={20} className="shrink-0 text-blue-600 dark:text-blue-400" />
                        <p>
                            {retentionPlan
                                ? 'Creating an offer does not show it to anyone yet. Put it in use for its plan from the Retention tab.'
                                : 'Creating an offer does not assign it to anyone or start a subscription. Assign it later in the Customers tab.'}
                        </p>
                    </div>
                    <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                        Google Play uses in-app subscription offers, not shareable promo codes. App Store offers require a separate code batch.
                    </p>
                </aside>
            </div>
        </div>
    )
}
